import {
  Box,
  Button,
  FormField,
  Modal,
  SpaceBetween,
  Textarea,
  TextareaProps,
} from "@amzn/awsui-components-console";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  MAX_SQL_LENGTH,
  SqlClassification,
  validateSqlSyntax,
} from "@/utils/sqlSyntax";

/**
 * The reason shown on a submit control disabled for a syntax rejection.
 *
 * Exported because it is asserted on: it is both the button's accessible
 * description and the field's visible error text, and those two must agree.
 */
export const INVALID_SQL_MESSAGE = "Invalid SQL";

/** The reason shown while no verdict has been reached yet. */
export const VALIDATING_MESSAGE = "Checking SQL syntax";

/** The reason shown when the text is longer than the endpoint will accept. */
export const TOO_LONG_MESSAGE = `SQL must be ${MAX_SQL_LENGTH.toLocaleString(
  "en-US",
)} characters or fewer`;

/**
 * Quiet period before re-classifying edited text.
 *
 * Measured from the most recent change, so holding a key or pasting a long
 * query classifies once at the end rather than once per keystroke.
 */
export const CLASSIFY_DEBOUNCE_MS = 300;

/** Rows on the editing control. Tall enough for a real query without scrolling. */
const TEXTAREA_ROWS = 12;

export interface SQLEditModalProps {
  /** Whether the modal is open. Owned by the caller. */
  visible: boolean;
  /**
   * The entry's **stored** SQL, which is what the field is seeded from.
   *
   * Deliberately not the formatted or truncated form the card displays: the
   * card runs its query through `sql-formatter` and shows the first five lines,
   * and seeding an editor from either would make an admin's first submission
   * silently rewrite their query into the display form.
   */
  sqlQuery: string;
  /** Close without submitting. Called for cancel, dismiss, and Escape. */
  onDismiss: () => void;
  /**
   * Submit the field's text.
   *
   * Resolve to close the modal, reject to keep it open with the text intact.
   * The modal's close is driven by the promise's outcome rather than by a flag
   * the caller has to remember to set, so a caller cannot report failure and
   * accidentally discard the admin's edit at the same time.
   */
  onSubmit: (sqlQuery: string) => Promise<void>;
  /**
   * The classifier. Injected so the component is fully testable without the
   * parser, and so a test can hold a classification open to drive supersession.
   */
  validateSql?: (text: string) => Promise<SqlClassification>;
  /**
   * The control that opened the modal. Focus returns here on every close route.
   *
   * A ref rather than a callback because it is read imperatively at close time
   * and never rendered.
   */
  returnFocusRef?: React.RefObject<{ focus: () => void } | null>;
}

/**
 * Modal for editing one cached message's stored SQL query.
 *
 * Composes Cloudscape's `Modal` directly rather than going through the shared
 * `useModal` hook. That hook passes only `disabled` and `loading` to its primary
 * button with no `disabledReason` passthrough, and does no focus management --
 * each of its callers focuses its own field and none of them returns focus on
 * close. Both are contracts here, so the modal is its own component. What
 * `Modal` does supply is used rather than rebuilt: the dialog role, the
 * accessible name from `header`, the focus trap, and Escape-to-dismiss.
 *
 * One framework behaviour changes the shape of the submit gate, and it was
 * verified in the installed component's source rather than inferred from its
 * documentation. A `Button` given both `disabled` and `disabledReason` does
 * **not** receive the native `disabled` attribute -- the internal render passes
 * `disabled: disabled && !__focusable && !isDisabledWithReason` -- and instead
 * gets `aria-disabled="true"`. Because the native attribute is absent the button
 * stays in the tab order, which is what makes "hover **or keyboard-focus** to
 * learn why" reachable at all; a natively disabled button cannot be focused and
 * so cannot show a tooltip. Activation is suppressed inside the component's own
 * click handler (`if (isNotInteractive) return event.preventDefault()`), so a
 * keyboard press does not reach `onClick` today. The submit handler re-checks
 * the gate regardless, because "no request leaves for text that is not
 * classified valid" is a promise about requests, not about a framework's
 * internals -- and that internal is a patch-version away from changing.
 */
const SQLEditModal: React.FC<SQLEditModalProps> = ({
  visible,
  sqlQuery,
  onDismiss,
  onSubmit,
  validateSql = validateSqlSyntax,
  returnFocusRef,
}) => {
  // Rendered, so state. Each of these three drives what the admin sees.
  const [sqlText, setSqlText] = useState("");
  const [classification, setClassification] =
    useState<SqlClassification>("unclassified");
  const [submitting, setSubmitting] = useState(false);

  // Read imperatively and never rendered, so refs. `useState` for the timer id
  // would re-render the modal -- and every consumer of its state -- on each
  // debounce write, which is a render per keystroke for a value nobody displays.
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const textareaRef = useRef<TextareaProps.Ref | null>(null);
  /** Whether the modal was open on the previous commit, for the close transition. */
  const wasVisibleRef = useRef(false);

  /**
   * Whether the field has actually mounted.
   *
   * State, not a ref, and that is the whole point: focusing the field has to
   * happen in a commit where the field exists, and only a state write schedules
   * one. `Modal` renders its contents through a portal -- its own source notes
   * the portal "needs extra cycles to render the inner components" -- so on the
   * commit where `visible` flips to true the field is not in the tree yet and a
   * ref to it is still null. A ref write here would record that the field
   * arrived and schedule nothing, so the focus effect would never re-run and
   * focus would be left wherever the framework put it.
   *
   * This is the one place a ref is the wrong primitive; the debounce timer id
   * above stays a ref because nothing needs to happen when it changes.
   */
  const [fieldMounted, setFieldMounted] = useState(false);

  // The validator is held in a ref rather than read from props inside the
  // classification effect, and deliberately kept out of that effect's
  // dependencies. It is an injection seam, not reactive data: a caller that
  // passes an inline arrow would otherwise supply a new identity on every
  // render and restart the debounce window each time, so a parent re-rendering
  // for unrelated reasons could keep a verdict permanently 300 ms away.
  const validateSqlRef = useRef(validateSql);
  validateSqlRef.current = validateSql;

  const close = useCallback(() => {
    // Only asks the caller to close. Returning focus is deliberately *not* done
    // here: see the close-transition effect below for why doing it synchronously
    // does not survive.
    onDismiss();
  }, [onDismiss]);

  // Seed the field from the stored value each time the modal opens, and each
  // time the stored value changes underneath an open modal. Keyed on both, so a
  // cancelled edit cannot survive into the next opening: reopening re-seeds
  // rather than resuming.
  useEffect(() => {
    if (!visible) {
      return;
    }
    setSqlText(sqlQuery);
    // Until a verdict arrives the submit control must be disabled, so the
    // unclassified state is set here rather than left holding a verdict that
    // belonged to the previous opening's text.
    setClassification("unclassified");
  }, [visible, sqlQuery]);

  // Classify the current text. Immediately for the seeded value, so an already
  // valid stored query has an enabled submit control with no edit; debounced for
  // anything the admin typed or pasted.
  useEffect(() => {
    if (!visible) {
      return;
    }

    // Closure-local, NOT a shared ref. This is the guard that decides whether a
    // resolving classification is still relevant, and a ref shared across effect
    // runs would be reset to `false` by the next run -- letting a superseded
    // classification write its verdict over the current text's. Scoped to this
    // one effect invocation, a superseded run can never pass the check.
    let cancelled = false;

    const delay = sqlText === sqlQuery ? 0 : CLASSIFY_DEBOUNCE_MS;

    timerRef.current = setTimeout(() => {
      void (async () => {
        let result: SqlClassification;
        try {
          result = await validateSqlRef.current(sqlText);
        } catch {
          // A classifier that throws is not a syntax rejection. Reporting it as
          // one would tell the admin their SQL is wrong on no evidence, so it
          // reads as "no verdict" -- which keeps submission disabled without
          // making a claim about the text.
          result = "unclassified";
        }
        if (!cancelled) {
          setClassification(result);
        }
      })();
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [visible, sqlText, sqlQuery]);

  // Focus the field once it exists, so an admin lands in the editor rather than
  // on the modal's dismiss control.
  //
  // Keyed on `fieldMounted` as well as `visible` because of the portal noted
  // above: on the commit where `visible` becomes true there is nothing to focus.
  // Running again once the field mounts also puts this *after* the modal's own
  // focus lock, which focuses the first focusable descendant -- the dismiss
  // control -- when it is enabled. This component is an ancestor of that lock,
  // so its effects run after the lock's, and the lock does not re-run on the
  // later commit. That ordering is what makes this win rather than race.
  useEffect(() => {
    if (visible && fieldMounted) {
      textareaRef.current?.focus();
    }
  }, [visible, fieldMounted]);

  // Return focus to the control that opened the modal, on every close route --
  // cancel, dismiss, Escape, and a successful submission all flow through
  // `close`, and a caller that hides the modal for its own reasons lands here
  // too.
  //
  // In an effect on the close transition rather than inside `close` itself. The
  // modal's focus lock restores focus to whatever was focused before it opened
  // when it becomes disabled, and that restore runs in the commit that applies
  // `visible: false`. A synchronous `focus()` inside `close` happens before that
  // commit, so the lock overwrites it and focus ends up on the document body.
  // Running here, after the lock, is what makes the return stick.
  useEffect(() => {
    if (wasVisibleRef.current && !visible) {
      returnFocusRef?.current?.focus();
    }
    wasVisibleRef.current = visible;
  }, [visible, returnFocusRef]);

  const attachField = useCallback((node: TextareaProps.Ref | null) => {
    textareaRef.current = node;
    setFieldMounted(node !== null);
  }, []);

  const handleChange = useCallback(
    ({ detail }: { detail: TextareaProps.ChangeDetail }) => {
      // The unclassified state is set here, on the change itself, rather than
      // waiting for the debounce to fire. Otherwise the submit control keeps the
      // previous text's verdict for the whole 300 ms window, which is long
      // enough to click -- submitting text nothing has classified.
      setClassification("unclassified");
      // Passed through untouched: no trim, no formatter, no auto-indent.
      setSqlText(detail.value);
    },
    [],
  );

  const submitDisabled = classification !== "valid" || submitting;

  const disabledReason = (() => {
    if (submitting || classification === "valid") {
      // `loading` already conveys the in-flight state; a tooltip explaining a
      // disabled control would contradict it.
      return undefined;
    }
    if (classification === "invalid") {
      return INVALID_SQL_MESSAGE;
    }
    if (classification === "tooLong") {
      return TOO_LONG_MESSAGE;
    }
    return VALIDATING_MESSAGE;
  })();

  // Only an actual verdict is shown as an error. "No verdict yet" is not a
  // problem with the text and must not be presented as one.
  const errorText =
    classification === "invalid"
      ? INVALID_SQL_MESSAGE
      : classification === "tooLong"
        ? TOO_LONG_MESSAGE
        : undefined;

  const handleSubmit = useCallback(async () => {
    // Unreachable today, and kept deliberately. Two independent things already
    // stop a non-valid submission before this line: the button suppresses its
    // own activation while disabled, and `handleChange` drops the verdict to
    // unclassified synchronously, so no window exists in which this handler runs
    // with a stale valid verdict. Both the coverage report (these two lines are
    // the file's only uncovered ones) and a mutation run that deletes the check
    // and still passes every test say so.
    //
    // It stays because "no request leaves for text that is not classified valid"
    // is a promise about requests, and without this the promise rests entirely on
    // a framework internal -- a `preventDefault` inside a vendored button's click
    // handler -- that a patch release can change without any test here noticing.
    // Three lines is a cheap place to keep that promise locally true.
    if (classification !== "valid" || submitting) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(sqlText);
      close();
    } catch {
      // Leave the field editable with its text intact so the admin can correct
      // and retry. The caller owns surfacing the reason; swallowing here also
      // keeps the button's click handler from producing an unhandled rejection.
    } finally {
      setSubmitting(false);
    }
  }, [classification, submitting, onSubmit, sqlText, close]);

  return (
    <Modal
      visible={visible}
      // Escape and the dismiss control share the cancel path. Ignored while a
      // submission is in flight, which is what stops Escape from abandoning a
      // request whose outcome the admin still needs to see.
      onDismiss={() => {
        if (!submitting) {
          close();
        }
      }}
      header="Edit SQL query"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={close}
              disabled={submitting}
              data-testid="sql-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleSubmit()}
              disabled={submitDisabled}
              disabledReason={disabledReason}
              loading={submitting}
              data-testid="sql-edit-submit"
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label="SQL query"
        description="The query this cached message answers with."
        errorText={errorText}
        stretch
      >
        <Textarea
          ref={attachField}
          value={sqlText}
          onChange={handleChange}
          rows={TEXTAREA_ROWS}
          // No maxLength: the control accepts over-length text so the length
          // problem is reported as a reason the admin can read, rather than the
          // field silently swallowing keystrokes at the boundary.
          spellcheck={false}
          data-testid="sql-edit-textarea"
        />
      </FormField>
    </Modal>
  );
};

export default SQLEditModal;
